import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api, { dashboardAPI } from '../lib/api';
import { formatCurrency, formatRelativeTime, getStatusColor } from '../lib/utils';
import { 
  Users, 
  Briefcase, 
  CheckSquare, 
  UserPlus,
  Clock,
  Calendar,
  ArrowRight,
  Settings2,
  GripVertical,
  Plus,
  DollarSign,
  FileText,
  Bell,
  PieChart,
  LayoutGrid,
  Move,
  Save,
  Trash2,
  BookMarked
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { PageLoader } from '../components/common/LoadingSpinner';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Widget configurations
const WIDGET_CONFIGS = {
  stats: {
    id: 'stats',
    title: 'Key Statistics',
    icon: LayoutGrid,
    description: 'Overview of clients, cases, tasks, and leads',
    defaultEnabled: true,
    size: 'full'
  },
  recent_cases: {
    id: 'recent_cases',
    title: 'Recent Cases',
    icon: Briefcase,
    description: 'Latest case activity',
    defaultEnabled: true,
    size: 'large'
  },
  upcoming_events: {
    id: 'upcoming_events',
    title: 'Upcoming Events',
    icon: Calendar,
    description: 'Calendar events and deadlines',
    defaultEnabled: true,
    size: 'small'
  },
  recent_tasks: {
    id: 'recent_tasks',
    title: 'Recent Tasks',
    icon: CheckSquare,
    description: 'Latest task assignments',
    defaultEnabled: true,
    size: 'full'
  },
  quick_actions: {
    id: 'quick_actions',
    title: 'Quick Actions',
    icon: Plus,
    description: 'Shortcuts to common actions',
    defaultEnabled: false,
    size: 'small'
  },
  revenue_summary: {
    id: 'revenue_summary',
    title: 'Revenue Summary',
    icon: DollarSign,
    description: 'Financial overview',
    defaultEnabled: false,
    size: 'medium'
  },
  document_activity: {
    id: 'document_activity',
    title: 'Document Activity',
    icon: FileText,
    description: 'Recent document uploads',
    defaultEnabled: false,
    size: 'medium'
  },
  reminders: {
    id: 'reminders',
    title: 'Upcoming Reminders',
    icon: Bell,
    description: 'Pending reminders and notifications',
    defaultEnabled: false,
    size: 'small'
  },
  billable_hours: {
    id: 'billable_hours',
    title: 'Billable Hours',
    icon: Clock,
    description: 'Time tracking summary',
    defaultEnabled: false,
    size: 'medium'
  },
  case_distribution: {
    id: 'case_distribution',
    title: 'Case Distribution',
    icon: PieChart,
    description: 'Cases by status and type',
    defaultEnabled: false,
    size: 'medium'
  }
};

const DEFAULT_LAYOUT = ['stats', 'recent_cases', 'upcoming_events', 'recent_tasks'];

// Sortable Widget Item for the customize dialog
const SortableWidgetItem = ({ widget, isEnabled, onToggle }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: widget.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const Icon = widget.icon;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 ${
        isDragging ? 'ring-2 ring-blue-500 bg-blue-50' : ''
      } ${isEnabled ? 'border-blue-200 bg-blue-50/30' : ''}`}
    >
      <div className="flex items-center gap-3">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-200 rounded touch-none"
          data-testid={`drag-handle-${widget.id}`}
        >
          <GripVertical className="h-4 w-4 text-slate-400" />
        </button>
        <Icon className="h-5 w-5 text-slate-500" />
        <div>
          <p className="text-sm font-medium">{widget.title}</p>
          <p className="text-xs text-slate-500">{widget.description}</p>
        </div>
      </div>
      <Switch 
        checked={isEnabled}
        onCheckedChange={() => onToggle(widget.id)}
      />
    </div>
  );
};

// Draggable Widget Wrapper for the dashboard
const DraggableWidget = ({ id, children, isEditMode }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  if (!isEditMode) {
    return <>{children}</>;
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative ${isDragging ? 'z-50 opacity-75' : ''}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="absolute -top-2 -right-2 z-10 bg-blue-600 text-white p-1.5 rounded-full cursor-grab active:cursor-grabbing shadow-lg hover:bg-blue-700 transition-colors"
        data-testid={`widget-drag-${id}`}
      >
        <Move className="h-3 w-3" />
      </div>
      <div className={`${isDragging ? 'ring-2 ring-blue-500 rounded-lg' : ''}`}>
        {children}
      </div>
    </div>
  );
};

// Predefined presets
const DASHBOARD_PRESETS = {
  default: {
    name: 'Default',
    widgets: ['stats', 'recent_cases', 'upcoming_events', 'recent_tasks'],
    order: ['stats', 'recent_cases', 'upcoming_events', 'recent_tasks']
  },
  minimal: {
    name: 'Minimal',
    widgets: ['stats', 'recent_cases'],
    order: ['stats', 'recent_cases']
  },
  full: {
    name: 'Full Dashboard',
    widgets: Object.keys(WIDGET_CONFIGS),
    order: Object.keys(WIDGET_CONFIGS)
  },
  finance_focused: {
    name: 'Finance Focused',
    widgets: ['stats', 'revenue_summary', 'billable_hours', 'recent_cases'],
    order: ['stats', 'revenue_summary', 'billable_hours', 'recent_cases']
  },
  task_focused: {
    name: 'Task Focused',
    widgets: ['stats', 'recent_tasks', 'upcoming_events', 'reminders'],
    order: ['stats', 'recent_tasks', 'upcoming_events', 'reminders']
  }
};

export const Dashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [widgetOrder, setWidgetOrder] = useState(() => {
    const saved = localStorage.getItem('dashboard_widget_order');
    return saved ? JSON.parse(saved) : Object.keys(WIDGET_CONFIGS);
  });
  const [enabledWidgets, setEnabledWidgets] = useState(() => {
    const saved = localStorage.getItem('dashboard_widgets');
    return saved ? JSON.parse(saved) : DEFAULT_LAYOUT;
  });
  const [reminders, setReminders] = useState([]);
  const [recentDocuments, setRecentDocuments] = useState([]);
  const [billableData, setBillableData] = useState(null);
  const [customPresets, setCustomPresets] = useState(() => {
    const saved = localStorage.getItem('dashboard_custom_presets');
    return saved ? JSON.parse(saved) : {};
  });
  const [currentPreset, setCurrentPreset] = useState(() => {
    return localStorage.getItem('dashboard_current_preset') || 'custom';
  });
  const [savePresetName, setSavePresetName] = useState('');
  const [showSavePreset, setShowSavePreset] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [dashboardRes] = await Promise.all([
          dashboardAPI.getStats()
        ]);
        setData(dashboardRes.data);
        
        if (enabledWidgets.includes('reminders')) {
          try {
            const remRes = await api.get('/reminders/upcoming?days=7');
            setReminders(remRes.data);
          } catch (e) { /* optional widget */ }
        }
        
        if (enabledWidgets.includes('document_activity')) {
          try {
            const docRes = await api.get('/documents?limit=5');
            setRecentDocuments(docRes.data);
          } catch (e) { /* optional widget */ }
        }
        
        if (enabledWidgets.includes('billable_hours')) {
          try {
            const billRes = await api.get('/time-entries?limit=100');
            const entries = billRes.data || [];
            const totalHours = entries.reduce((sum, e) => sum + (e.hours || 0), 0);
            const billableHours = entries.filter(e => e.billable).reduce((sum, e) => sum + (e.hours || 0), 0);
            setBillableData({ totalHours, billableHours, entries: entries.slice(0, 5) });
          } catch (e) { /* optional widget */ }
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [enabledWidgets]);

  const savePreferences = useCallback((widgets, order) => {
    setEnabledWidgets(widgets);
    setWidgetOrder(order);
    localStorage.setItem('dashboard_widgets', JSON.stringify(widgets));
    localStorage.setItem('dashboard_widget_order', JSON.stringify(order));
    setCurrentPreset('custom');
    localStorage.setItem('dashboard_current_preset', 'custom');
  }, []);

  const toggleWidget = (widgetId) => {
    const newWidgets = enabledWidgets.includes(widgetId)
      ? enabledWidgets.filter(id => id !== widgetId)
      : [...enabledWidgets, widgetId];
    savePreferences(newWidgets, widgetOrder);
  };

  // Load a preset
  const loadPreset = useCallback((presetKey) => {
    const preset = DASHBOARD_PRESETS[presetKey] || customPresets[presetKey];
    if (preset) {
      setEnabledWidgets(preset.widgets);
      setWidgetOrder(preset.order);
      localStorage.setItem('dashboard_widgets', JSON.stringify(preset.widgets));
      localStorage.setItem('dashboard_widget_order', JSON.stringify(preset.order));
      setCurrentPreset(presetKey);
      localStorage.setItem('dashboard_current_preset', presetKey);
    }
  }, [customPresets]);

  // Save current config as a preset
  const saveCurrentAsPreset = useCallback(() => {
    if (!savePresetName.trim()) return;
    
    const key = savePresetName.toLowerCase().replace(/\s+/g, '_');
    const newPreset = {
      name: savePresetName,
      widgets: [...enabledWidgets],
      order: [...widgetOrder]
    };
    
    const updated = { ...customPresets, [key]: newPreset };
    setCustomPresets(updated);
    localStorage.setItem('dashboard_custom_presets', JSON.stringify(updated));
    setCurrentPreset(key);
    localStorage.setItem('dashboard_current_preset', key);
    setSavePresetName('');
    setShowSavePreset(false);
  }, [savePresetName, enabledWidgets, widgetOrder, customPresets]);

  // Delete a custom preset
  const deleteCustomPreset = useCallback((key) => {
    const updated = { ...customPresets };
    delete updated[key];
    setCustomPresets(updated);
    localStorage.setItem('dashboard_custom_presets', JSON.stringify(updated));
    if (currentPreset === key) {
      setCurrentPreset('custom');
      localStorage.setItem('dashboard_current_preset', 'custom');
    }
  }, [customPresets, currentPreset]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = widgetOrder.indexOf(active.id);
      const newIndex = widgetOrder.indexOf(over.id);
      const newOrder = arrayMove(widgetOrder, oldIndex, newIndex);
      savePreferences(enabledWidgets, newOrder);
    }
  };

  const handleDashboardDragEnd = (event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const activeWidgets = widgetOrder.filter(id => enabledWidgets.includes(id));
      const oldIndex = activeWidgets.indexOf(active.id);
      const newIndex = activeWidgets.indexOf(over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newActiveOrder = arrayMove(activeWidgets, oldIndex, newIndex);
        // Reconstruct full order maintaining disabled widgets
        const disabledWidgets = widgetOrder.filter(id => !enabledWidgets.includes(id));
        const newOrder = [...newActiveOrder, ...disabledWidgets];
        savePreferences(enabledWidgets, newOrder);
      }
    }
  };

  const resetToDefault = () => {
    savePreferences(DEFAULT_LAYOUT, Object.keys(WIDGET_CONFIGS));
  };

  if (loading) {
    return <PageLoader text="Loading dashboard..." />;
  }

  const stats = data?.stats || {
    total_clients: 0,
    active_cases: 0,
    pending_tasks: 0,
    open_leads: 0,
  };

  const statCards = [
    { label: 'Total Clients', value: stats.total_clients, icon: Users, color: 'text-blue-600', bgColor: 'bg-blue-50', link: '/clients' },
    { label: 'Active Cases', value: stats.active_cases, icon: Briefcase, color: 'text-green-600', bgColor: 'bg-green-50', link: '/cases' },
    { label: 'Pending Tasks', value: stats.pending_tasks, icon: CheckSquare, color: 'text-orange-600', bgColor: 'bg-orange-50', link: '/tasks' },
    { label: 'Open Leads', value: stats.open_leads, icon: UserPlus, color: 'text-purple-600', bgColor: 'bg-purple-50', link: '/leads' },
  ];

  // Widget Render Functions
  const renderStatsWidget = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="stats-grid">
      {statCards.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <Link to={stat.link} key={index}>
            <Card className="hover-lift cursor-pointer hover:border-blue-300 transition-colors" data-testid={`stat-card-${index}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">{stat.label}</p>
                    <p className="text-3xl font-bold text-slate-900 mt-2">{stat.value}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        );
      })}
    </div>
  );

  const renderRecentCasesWidget = () => (
    <Card className="lg:col-span-2" data-testid="recent-cases-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-[&apos;Plus_Jakarta_Sans&apos;]">Recent Cases</CardTitle>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/cases" className="gap-1">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        {data?.recent_cases?.length > 0 ? (
          <div className="space-y-4">
            {data.recent_cases.map((caseItem) => (
              <div 
                key={caseItem.case_id} 
                className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Briefcase className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{caseItem.title}</p>
                    <p className="text-sm text-slate-500">{caseItem.case_number}</p>
                  </div>
                </div>
                <Badge className={getStatusColor(caseItem.status)}>
                  {caseItem.status}
                </Badge>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <Briefcase className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p>No cases yet</p>
            <Button variant="link" asChild className="mt-2">
              <Link to="/cases">Create your first case</Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderUpcomingEventsWidget = () => (
    <Card data-testid="upcoming-events-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Upcoming</CardTitle>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/calendar" className="gap-1">
            <Calendar className="h-4 w-4" />
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        {data?.upcoming_events?.length > 0 ? (
          <div className="space-y-4">
            {data.upcoming_events.map((event) => (
              <div key={event.event_id} className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Clock className="h-4 w-4 text-purple-600" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">{event.title}</p>
                  <p className="text-sm text-slate-500">
                    {new Date(event.start_time).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <Calendar className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p>No upcoming events</p>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderRecentTasksWidget = () => (
    <Card data-testid="recent-tasks-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Recent Tasks</CardTitle>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/tasks" className="gap-1">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        {data?.recent_tasks?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_tasks.map((task) => (
                  <tr key={task.task_id}>
                    <td className="font-medium">{task.title}</td>
                    <td>
                      <Badge variant="outline" className="capitalize">
                        {task.priority}
                      </Badge>
                    </td>
                    <td>
                      <Badge className={getStatusColor(task.status)}>
                        {task.status}
                      </Badge>
                    </td>
                    <td className="text-slate-500">
                      {formatRelativeTime(task.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <CheckSquare className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p>No tasks yet</p>
            <Button variant="link" asChild className="mt-2">
              <Link to="/tasks">Create your first task</Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderQuickActionsWidget = () => (
    <Card data-testid="quick-actions-card">
      <CardHeader>
        <CardTitle className="text-lg">Quick Actions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <Button variant="outline" className="w-full justify-start gap-2" asChild>
          <Link to="/cases"><Briefcase className="h-4 w-4" /> New Case</Link>
        </Button>
        <Button variant="outline" className="w-full justify-start gap-2" asChild>
          <Link to="/clients"><Users className="h-4 w-4" /> Add Client</Link>
        </Button>
        <Button variant="outline" className="w-full justify-start gap-2" asChild>
          <Link to="/tasks"><CheckSquare className="h-4 w-4" /> Create Task</Link>
        </Button>
        <Button variant="outline" className="w-full justify-start gap-2" asChild>
          <Link to="/time-billing"><Clock className="h-4 w-4" /> Log Time</Link>
        </Button>
        <Button variant="outline" className="w-full justify-start gap-2" asChild>
          <Link to="/invoices"><DollarSign className="h-4 w-4" /> New Invoice</Link>
        </Button>
      </CardContent>
    </Card>
  );

  const renderRemindersWidget = () => (
    <Card data-testid="reminders-widget">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Reminders</CardTitle>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/reminders"><Bell className="h-4 w-4" /></Link>
        </Button>
      </CardHeader>
      <CardContent>
        {reminders.length > 0 ? (
          <div className="space-y-3">
            {reminders.slice(0, 5).map((reminder) => (
              <div key={reminder.reminder_id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50">
                <Bell className="h-4 w-4 text-orange-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium">{reminder.title}</p>
                  <p className="text-xs text-slate-500">
                    {new Date(reminder.due_date).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-slate-500 py-4">No upcoming reminders</p>
        )}
      </CardContent>
    </Card>
  );

  const renderBillableHoursWidget = () => (
    <Card data-testid="billable-hours-widget">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Billable Hours</CardTitle>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/time-billing"><Clock className="h-4 w-4" /></Link>
        </Button>
      </CardHeader>
      <CardContent>
        {billableData ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-slate-600">Total Hours</p>
                <p className="text-2xl font-bold text-blue-600">{billableData.totalHours.toFixed(1)}</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-slate-600">Billable</p>
                <p className="text-2xl font-bold text-green-600">{billableData.billableHours.toFixed(1)}</p>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-center text-slate-500 py-4">No time entries</p>
        )}
      </CardContent>
    </Card>
  );

  const renderDocumentActivityWidget = () => (
    <Card data-testid="document-activity-widget">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Recent Documents</CardTitle>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/documents"><FileText className="h-4 w-4" /></Link>
        </Button>
      </CardHeader>
      <CardContent>
        {recentDocuments.length > 0 ? (
          <div className="space-y-3">
            {recentDocuments.slice(0, 5).map((doc) => (
              <div key={doc.document_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                <FileText className="h-4 w-4 text-blue-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{doc.name}</p>
                  <p className="text-xs text-slate-500">
                    {formatRelativeTime(doc.created_at)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-slate-500 py-4">No recent documents</p>
        )}
      </CardContent>
    </Card>
  );

  const renderRevenueSummaryWidget = () => (
    <Card data-testid="revenue-summary-widget">
      <CardHeader>
        <CardTitle className="text-lg">Revenue Summary</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div className="p-3 bg-green-50 rounded-lg">
            <p className="text-sm text-slate-600">This Month</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(data?.revenue?.this_month || 0)}</p>
          </div>
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-slate-600">Outstanding</p>
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(data?.revenue?.outstanding || 0)}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderCaseDistributionWidget = () => (
    <Card data-testid="case-distribution-widget">
      <CardHeader>
        <CardTitle className="text-lg">Case Distribution</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {['open', 'active', 'pending', 'closed'].map((status) => {
            const count = data?.case_distribution?.[status] || 0;
            const total = stats.active_cases || 1;
            const percentage = Math.min((count / total) * 100, 100);
            const colors = {
              open: 'bg-blue-500',
              active: 'bg-green-500',
              pending: 'bg-yellow-500',
              closed: 'bg-slate-400'
            };
            return (
              <div key={status} className="flex items-center gap-3">
                <span className="text-sm capitalize w-20">{status}</span>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${colors[status]}`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <span className="text-sm font-medium w-8">{count}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );

  // Widget renderer mapping
  const widgetRenderers = {
    stats: renderStatsWidget,
    recent_cases: renderRecentCasesWidget,
    upcoming_events: renderUpcomingEventsWidget,
    recent_tasks: renderRecentTasksWidget,
    quick_actions: renderQuickActionsWidget,
    reminders: renderRemindersWidget,
    billable_hours: renderBillableHoursWidget,
    document_activity: renderDocumentActivityWidget,
    revenue_summary: renderRevenueSummaryWidget,
    case_distribution: renderCaseDistributionWidget
  };

  // Get ordered widgets that are enabled
  const orderedEnabledWidgets = widgetOrder.filter(id => enabledWidgets.includes(id));
  const fullWidthWidgets = orderedEnabledWidgets.filter(id => 
    WIDGET_CONFIGS[id]?.size === 'full'
  );
  const gridWidgets = orderedEnabledWidgets.filter(id => 
    WIDGET_CONFIGS[id]?.size !== 'full'
  );

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Welcome Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-slate-500 mt-1">Here&apos;s what&apos;s happening with your practice today.</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant={isEditMode ? "default" : "outline"} 
            size="sm" 
            onClick={() => setIsEditMode(!isEditMode)}
            className={`gap-2 ${isEditMode ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
            data-testid="edit-layout-btn"
          >
            <Move className="h-4 w-4" />
            {isEditMode ? 'Done Editing' : 'Edit Layout'}
          </Button>
          <Dialog open={customizeOpen} onOpenChange={setCustomizeOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2" data-testid="customize-dashboard-btn">
                <Settings2 className="h-4 w-4" />
                Customize
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle>Customize Dashboard</DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto py-4 space-y-6">
                {/* Presets Section */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <BookMarked className="h-4 w-4 text-slate-500" />
                    <h3 className="text-sm font-medium">Dashboard Presets</h3>
                  </div>
                  
                  <div className="flex gap-2 mb-3">
                    <Select value={currentPreset} onValueChange={loadPreset}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select a preset" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="custom">Custom Layout</SelectItem>
                        {Object.entries(DASHBOARD_PRESETS).map(([key, preset]) => (
                          <SelectItem key={key} value={key}>{preset.name}</SelectItem>
                        ))}
                        {Object.entries(customPresets).length > 0 && (
                          <>
                            <SelectItem disabled value="---">─── Saved Presets ───</SelectItem>
                            {Object.entries(customPresets).map(([key, preset]) => (
                              <SelectItem key={key} value={key}>{preset.name}</SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Save as Preset */}
                  {showSavePreset ? (
                    <div className="flex gap-2">
                      <Input 
                        placeholder="Preset name..."
                        value={savePresetName}
                        onChange={(e) => setSavePresetName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && saveCurrentAsPreset()}
                      />
                      <Button size="sm" onClick={saveCurrentAsPreset} disabled={!savePresetName.trim()}>
                        <Save className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setShowSavePreset(false); setSavePresetName(''); }}>
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => setShowSavePreset(true)} className="w-full">
                      <Save className="h-4 w-4 mr-2" />
                      Save Current as Preset
                    </Button>
                  )}
                  
                  {/* Custom presets with delete */}
                  {Object.keys(customPresets).length > 0 && (
                    <div className="mt-3 space-y-1">
                      <p className="text-xs text-slate-500">Your saved presets:</p>
                      {Object.entries(customPresets).map(([key, preset]) => (
                        <div key={key} className="flex items-center justify-between text-sm p-2 bg-slate-50 rounded">
                          <span>{preset.name}</span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => {
                              deleteCustomPreset(key);
                              toast.success('Preset deleted');
                            }}
                          >
                            <Trash2 className="h-3 w-3 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="border-t pt-4">
                  <p className="text-sm text-slate-500 mb-4">
                    Toggle widgets and drag to reorder. Enabled widgets will appear on your dashboard.
                  </p>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext items={widgetOrder} strategy={rectSortingStrategy}>
                      <div className="space-y-2">
                        {widgetOrder.map((widgetId) => {
                          const widget = WIDGET_CONFIGS[widgetId];
                          if (!widget) return null;
                          return (
                            <SortableWidgetItem
                              key={widget.id}
                              widget={widget}
                              isEnabled={enabledWidgets.includes(widget.id)}
                              onToggle={toggleWidget}
                            />
                          );
                        })}
                      </div>
                    </SortableContext>
                  </DndContext>
                </div>
              </div>
              <div className="flex justify-between pt-4 border-t">
                <Button variant="ghost" size="sm" onClick={resetToDefault}>
                  Reset to Default
                </Button>
                <Button size="sm" onClick={() => setCustomizeOpen(false)}>
                  Done
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button asChild data-testid="quick-add-btn">
            <Link to="/cases" className="gap-2">
              <Briefcase className="h-4 w-4" />
              New Case
            </Link>
          </Button>
        </div>
      </div>

      {/* Edit Mode Instructions */}
      {isEditMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Move className="h-5 w-5 text-blue-600" />
          <div>
            <p className="font-medium text-blue-900">Edit Mode Active</p>
            <p className="text-sm text-blue-700">Drag the blue handles to reorder widgets. Click &quot;Done Editing&quot; when finished.</p>
          </div>
        </div>
      )}

      {/* Dashboard Content with Drag and Drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDashboardDragEnd}
      >
        <SortableContext items={orderedEnabledWidgets} strategy={rectSortingStrategy}>
          {/* Full-width widgets at the top */}
          {fullWidthWidgets.map((widgetId) => (
            <DraggableWidget key={widgetId} id={widgetId} isEditMode={isEditMode}>
              {widgetRenderers[widgetId]?.()}
            </DraggableWidget>
          ))}

          {/* Grid widgets */}
          {gridWidgets.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {gridWidgets.map((widgetId) => (
                <DraggableWidget key={widgetId} id={widgetId} isEditMode={isEditMode}>
                  {widgetRenderers[widgetId]?.()}
                </DraggableWidget>
              ))}
            </div>
          )}
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default Dashboard;
